import {useNavigate, useLocation} from 'react-router-dom';
import {useKeycloak} from '@react-keycloak/web';
import {useConfig} from '../contexts/ClientConfigContext';
import {useRoles} from '../hooks/useRoles';
import {surveyRoutes, diagnosticsRoutes} from '../routes/routesConfig';
import QuadralSidebar from '@quadral/quadral-component-common/dist/components/sidebar';
import AppSwitcher from './AppSwitcher';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
    faBars, faTableColumns, faPoll, faUsers, faChartBar,
    faFolder, faBuilding, faShoppingCart, faNewspaper,
    faChevronLeft, faChevronRight, faArrowRightFromBracket, faChevronDown
} from '@fortawesome/free-solid-svg-icons';

const ICONS = {
    faTableColumns: <FontAwesomeIcon icon={faTableColumns}/>,
    faPoll: <FontAwesomeIcon icon={faPoll}/>,
    faUsers: <FontAwesomeIcon icon={faUsers}/>,
    faChartBar: <FontAwesomeIcon icon={faChartBar}/>,
    faFolder: <FontAwesomeIcon icon={faFolder}/>,
    faBuilding: <FontAwesomeIcon icon={faBuilding}/>,
    faShoppingCart: <FontAwesomeIcon icon={faShoppingCart}/>,
    faNewspaper: <FontAwesomeIcon icon={faNewspaper}/>
};

const isActive = (route, pathname) => {
    if (route.path === '/enquetes' || route.path === '/diagnostics') {
        return pathname === route.path;
    }
    return pathname.startsWith(route.path);
};

const buildItems = (routes, pathname, hasAnyRole) =>
    routes
        .filter(route => route.roles?.length === 0 || hasAnyRole(route.roles))
        .map(route => {
            if (route.dropdown) {
                const visibleChildren = route.children
                    .filter(c => c.roles?.length === 0 || hasAnyRole(c.roles))
                    .map(c => ({...c, active: pathname === c.path, icon: ICONS[c.icon]}));
                if (!visibleChildren.length) return null;
                return {label: route.label, icon: ICONS[route.icon], children: visibleChildren};
            }
            return {
                ...route,
                icon: ICONS[route.icon],
                active: isActive(route, pathname),
            };
        })
        .filter(Boolean);

const Sidebar = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const {keycloak} = useKeycloak();
    const {hasAnyRole} = useRoles();
    const config = useConfig();

    const isSurvey = location.pathname.startsWith('/enquetes');
    const routes = isSurvey ? surveyRoutes : diagnosticsRoutes;
    const items = buildItems(routes, location.pathname, hasAnyRole);

    const username = keycloak.tokenParsed?.name || 'Utilisateur';
    const roles = keycloak.tokenParsed?.realm_access?.roles || [];
    const userRole = roles.includes('internal') ? 'Administrateur' : null;

    const currentApp = (config.apps || []).find(app =>
        app.url && location.pathname.startsWith(app.url)
    );

    return (
        <QuadralSidebar
            items={items}
            username={username}
            userRole={userRole}
            onNavigate={navigate}
            onLogout={() => keycloak.logout()}
            collapseIconLeft={<FontAwesomeIcon icon={faChevronLeft} style={{fontSize: 12}}/>}
            collapseIconRight={<FontAwesomeIcon icon={faChevronRight} style={{fontSize: 12}}/>}
            logoutIcon={<FontAwesomeIcon icon={faArrowRightFromBracket} style={{fontSize: 14}}/>}
            dropdownIcon={<FontAwesomeIcon icon={faChevronDown}/>}
            mobileMenuIcon={
                <FontAwesomeIcon icon={faBars} style={{fontSize: 18}}/>
            }
            header={
                (expanded) => <AppSwitcher currentApp={currentApp} expanded={expanded}/>
            }
        />
    );
};

export default Sidebar;